import React, { useEffect, useState } from "react";
import API from "../services/api";

const AssignedRequirements = () => {
  const [requirements, setRequirements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    fetchRequirements();
  }, []);

  const fetchRequirements = async () => {
    setLoading(true);
    try {
      const res = await API.get("/employee/my-requirements");
      setRequirements(Array.isArray(res.data) ? res.data : []);
      setError("");
    } catch (err) {
      setError("Failed to load requirements");
    } finally {
      setLoading(false);
    }
  };

  /* ---------- helpers ---------- */

  // Prefer unified vendor block from BE; fall back to old shapes
  const vendorDisplay = (req) => {
    if (req.vendor) {
      const name = req.vendor.companyName || req.vendor.fullName || "-";
      const mobile = req.vendor.mobile || "";
      const email = req.vendor.email || "";
      return { name, mobile, email };
    }
    const name =
      req.vendorId?.companyName ||
      req.vendorId?.userId?.fullName ||
      req.vendorId?.fullName ||
      "-";
    const mobile = req.vendorId?.userId?.mobile || req.vendorId?.mobile || "";
    const email = req.vendorId?.userId?.email || req.vendorId?.email || "";
    return { name, mobile, email };
  };

  // Compose a printable location cell with optional map link
  const locationCell = (req) => {
    // 1) server-composed text (we added this in employee controller)
    if (typeof req.locationText === "string" && req.locationText.trim()) {
      return req.locationText.trim();
    }
    // Some builds used this alias
    if (typeof req.displayLocation === "string" && req.displayLocation.trim()) {
      return req.displayLocation.trim();
    }

    const loc = req.location;
    if (!loc) return "N/A";

    // 2) legacy: plain string
    if (typeof loc === "string") return loc.trim() || "N/A";

    // 3) object: address pieces + lat/lng
    const {
      address,
      line1,
      area,
      city,
      state,
      pincode,
      lat,
      lng,
      latitude,
      longitude,
    } = loc || {};

    const text = [address, line1, area, city, state, pincode]
      .map((x) => (x || "").toString().trim())
      .filter(Boolean)
      .join(", ");

    const la = lat ?? latitude;
    const lo = lng ?? longitude;

    if (!text && (la == null || lo == null)) return "N/A";

    return (
      <>
        <div className="mb-1">{text || "N/A"}</div>
        {la != null && lo != null && (
          <a
            href={`https://www.google.com/maps?q=${la},${lo}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 underline"
          >
            📍 View
          </a>
        )}
      </>
    );
  };

  const fmtDate = (d) =>
    new Date(d).toLocaleDateString("en-IN", { timeZone: "Asia/Kolkata" });

  const requirementAmountOf = (req) =>
    Number(req?.requirementAmount ?? req?.payment?.latestAmountRupees ?? 0);

  return (
    <div className="p-6">
      <h2 className="text-xl font-bold mb-4">📋 Assigned Requirements</h2>

      {loading && <p>Loading...</p>}
      {error && <p className="text-red-500">{error}</p>}

      {!loading && !error && requirements.length > 0 ? (
        <table className="w-full border-collapse border border-gray-300 bg-white shadow-sm">
          <thead>
            <tr className="bg-gray-100">
              <th className="border px-4 py-2">#</th>
              <th className="border px-4 py-2">Vendor</th>
              <th className="border px-4 py-2">Product Type</th>
              <th className="border px-4 py-2">Quantity</th>
              <th className="border px-4 py-2">Unit</th>
              <th className="border px-4 py-2">Amount</th>
              <th className="border px-4 py-2">Location</th>
              <th className="border px-4 py-2">Notes</th>
              <th className="border px-4 py-2">Status</th>
              <th className="border px-4 py-2">Approval</th>
              <th className="border px-4 py-2">Assigned Date</th>
            </tr>
          </thead>
          <tbody>
            {requirements.map((req, i) => {
              const v = vendorDisplay(req);
              return (
                <tr key={req._id}>
                  <td className="border px-4 py-2">{i + 1}</td>
                  <td className="border px-4 py-2">
                    <div className="font-medium">{v.name}</div>
                    {v.mobile ? (
                      <div className="text-xs text-gray-600">
                        <a className="underline" href={`tel:${v.mobile}`}>
                          {v.mobile}
                        </a>
                      </div>
                    ) : null}
                    {v.email ? (
                      <div className="text-xs text-gray-600">
                        <a className="underline" href={`mailto:${v.email}`}>
                          {v.email}
                        </a>
                      </div>
                    ) : null}
                  </td>
                  <td className="border px-4 py-2">{req.productType || "-"}</td>
                  <td className="border px-4 py-2">{req.quantity ?? "-"}</td>
                  <td className="border px-4 py-2">{req.unit || "-"}</td>
                  <td className="border px-4 py-2">
                    {requirementAmountOf(req) > 0
                      ? `₹ ${requirementAmountOf(req).toLocaleString("en-IN")}`
                      : "-"}
                  </td>
                  <td className="border px-4 py-2">{locationCell(req)}</td>
                  <td className="border px-4 py-2">{req.notes || "-"}</td>
                  <td className="border px-4 py-2">{req.status || "-"}</td>
                  <td className="border px-4 py-2">
                    {req.approvalStatus || "-"}
                  </td>
                  <td className="border px-4 py-2">
                    {req.createdAt ? fmtDate(req.createdAt) : "-"}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      ) : (
        !loading && !error && <p>No requirements assigned yet.</p>
      )}
    </div>
  );
};

export default AssignedRequirements;
